//src/admin/features/clients/viewClients/AddNewClientButton.tsx
import { serviceTypeOptions } from 'app/features/profile/accountInfo/editAccountInfo/formFields';
import { useInviteCLientMutation } from 'app/services/user';
import { AddBlackIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';
import { PhoneNumInput } from 'Components/Auth/PhoneInput';
import { SelectInput } from 'Components/Auth/SelectInput';
import { TextInput } from 'Components/Auth/TextInput';
import Form from 'Components/Form';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import { useClosePopup } from 'Components/Popups';
import useCreateModal from 'Components/Popups/useCreateModal';
import LanguageSelector from 'Components/Selectors/LanguageSelector';
import TimezoneSelector from 'Components/Selectors/TimezoneSelector';
import { SubmitHandler } from 'react-hook-form';
import { Validators } from 'utils/Validators';
import * as Yup from 'yup';

export type InviteClientForm = {
  given_name: string;
  family_name: string;
  email: string | undefined;
  time_zone_id: number;
  language_id: number;
  address: string;
  zipcode: string; // Add Zip Code
  city: string; // Add City
  service_type: string;
  hear_about_us: string;
  phone_number: string | undefined;
  other: string;
  showEmail: boolean;
};

const validationSchema = Yup.object().shape({
  given_name: Yup.string().trim().required('required'),
  family_name: Yup.string().trim().required('required'),
  address: Yup.string().trim(),
  zipcode: Validators.zipCode,
  city: Yup.string().trim(),
  time_zone_id: Yup.number().required('required'),
  language_id: Yup.string().required('required'),
  service_type: Yup.string().required('required'),
  phone_number: Yup.string()
    .trim()
    .when('showEmail', {
      is: true,
      then: Yup.string().trim(),
      otherwise: Yup.string().trim().required('required'),
    }),
  email: Yup.string()
    .trim()
    .when('showEmail', {
      is: true,
      then: Yup.string().trim().email().required('required'),
      otherwise: Yup.string().trim(),
    }),
});

export function AddNewClientButton({
  refetchAfterAdd,
}: {
  refetchAfterAdd: () => void;
}) {
  const Modal = useCreateModal();

  return (
    <>
      <div
        onClick={Modal.open}
        className="flex whitespace-nowrap h-10 font-semibold cursor-pointer items-center text-sm px-3 py-2 text-white border border-green  bg-green rounded-lg"
      >
        <AddBlackIcon className="text-white w-4 mr-3" />
        <span>Invite New</span>
      </div>
      <Modal.Component placement="screen-offset-top">
        <InviteClientForm refetchAfterAdd={refetchAfterAdd} />
      </Modal.Component>
    </>
  );
}

function InviteClientForm({
  refetchAfterAdd,
}: {
  refetchAfterAdd: () => void;
}) {
  const closePopup = useClosePopup();

  const [InviteClient] = useInviteCLientMutation();

  const onSubmit: SubmitHandler<InviteClientForm> = (data) => {
    data.email = data.email === '' ? undefined : data.email?.toLowerCase();
    data.phone_number =
      data.phone_number === '' ? undefined : data.phone_number?.toLowerCase();
    data.address = data.address === '' ? 'TBD' : data.address;
    data.zipcode = data.zipcode === '' ? '00000' : data.zipcode; // Default if empty
    data.city = data.city === '' ? 'Unknown' : data.city;
    InviteClient({
      ...data,
      hear_about_us: data.other ? data.other : data.hear_about_us,
      showEmail: true,
    })
      .unwrap()
      .then((_) => {
        successToast('Client invitation sent');
        refetchAfterAdd();
        closePopup();
      })
      .catch((e) => errorToast(e.data.message));
  };

  return (
    <Form onSubmit={onSubmit} validationSchema={validationSchema}>
      {(methods) => (
        <OverlayWindow
          title={'Add New Client'}
          subTitle={`Enter client information`}
          onClose={closePopup}
          size={WindowSize.lg}
          body={
            <div className="grid gap-4 my-2 grid-cols-2">
              <TextInput
                name="given_name"
                label="Name"
                placeholder="First Name"
                type="text"
              />

              <TextInput
                name="family_name"
                placeholder="Last Name"
                label="Last Name"
                type="text"
              />
              <div>
                {!methods.getValues('showEmail') ? (
                  <PhoneNumInput name="phone_number" label="Phone Number" />
                ) : (
                  <TextInput
                    name="email"
                    label="Email Address"
                    placeholder="Enter your email..."
                    type="text"
                    autoComplete="false"
                  />
                )}
                <span
                  className="font-medium text-green	text-sm cursor-pointer block mb-7 mt-3"
                  onClick={() => {
                    methods.setValue(
                      'showEmail',
                      !methods.getValues('showEmail'),
                      {
                        shouldValidate: true,
                      },
                    );
                    methods.setValue('phone_number', '');
                    methods.setValue('email', '');
                  }}
                  ref={methods.register('showEmail').ref}
                >
                  {methods.getValues('showEmail')
                    ? 'Use phone number instead'
                    : 'Use email address instead'}
                </span>
              </div>

              <SelectInput
                name="service_type"
                label="Service Type"
                options={serviceTypeOptions}
                type="select"
                placeholder="Select Service Type"
              />

              <div className="col-span-2">
                <TextInput
                  name="address"
                  label="Address"
                  placeholder="Address"
                  type="text"
                  rules={{ required: 'required' }}
                />
              </div>
              <TextInput
                name="city"
                label="City"
                placeholder="Enter City"
                type="text"
                rules={{ required: 'required' }}
              />

              <TextInput
                name="zipcode"
                label="Zip Code"
                placeholder="Enter Zip Code"
                type="text"
                rules={{ required: 'required' }}
              />

              <TimezoneSelector />

              <LanguageSelector />
            </div>
          }
          footer={
            <div className=" flex justify-end gap-5 p-3 shadow-top">
              <div className="w-32">
                <CustomButton
                  bgColor="white"
                  textColor="gray-700"
                  text="Cancel"
                  borderColor="gray-100"
                  onClick={closePopup}
                />
              </div>
              <div className="w-32">
                <CustomButton type="submit" text={'Invite'} />
              </div>
            </div>
          }
        />
      )}
    </Form>
  );
}
